import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { AnalysisState } from "../hooks/use-resource-analysis-cache";
import { SelectedRowState } from "../patient-overview-v2-content";
import { AllergyRowData } from "../tables/allergy";
import { CareGapRowData } from "../tables/care-gaps";
import { CarePlanRowData } from "../tables/care-plan";
import { ConditionRowData } from "../tables/condition";
import { CoverageRowData } from "../tables/coverage";
import { EncounterRowData } from "../tables/encounter";
import { FamilyMemberHistoryRowData } from "../tables/family-history";
import { ImmunizationRowData } from "../tables/immunization";
import { LabRowData } from "../tables/lab";
import { MedicationRowData } from "../tables/medication";
import { DiagnosticReportRowData } from "../tables/notes/diagnostic-reports-rows";
import { ImagingReportRowData } from "../tables/notes/imaging-reports-rows";
import { ProcedureRowData } from "../tables/procedure";
import { RelatedPersonRowData } from "../tables/related-person";
import { SocialHistoryRowData } from "../tables/social-history";
import { SuspectRowData } from "../tables/suspect";
import { VitalRowData } from "../tables/vital";
import {
  AllergySidePanelContent,
  CarePlanSidePanelContent,
  ConditionSidePanelContent,
  CoverageSidePanelContent,
  EncounterSidePanelContent,
  FamilyHistorySidePanelContent,
  ImagingReportSidePanelContent,
  ImmunizationSidePanelContent,
  LabSidePanelContent,
  MedicationSidePanelContent,
  NoteSidePanelContent,
  ProcedureSidePanelContent,
  RelatedPersonSidePanelContent,
  SocialHistorySidePanelContent,
  SuspectSidePanelContent,
  VitalSidePanelContent,
} from "./index";
import { CareGapsSidePanelContent } from "./care-gaps-side-panel-content";
import { SidePanelContextProvider } from "./sidepanel-context";
import { SurveyRowData } from "../tables/survey";
import { SurveysSidePanelContent } from "./survey-side-panel-content";

/**
 * Renders the appropriate sidepanel content based on the selected row type
 */
export function SidePanelContent({
  selectedRowState,
  analysisState,
  fhirSdk,
  onAddToExport,
  onRemoveFromExport,
  isDocumentInExportList,
  onPdfView,
}: {
  selectedRowState: NonNullable<SelectedRowState>;
  analysisState: AnalysisState | undefined;
  fhirSdk: FhirBundleSdk;
  onAddToExport: (docId: string, docFilename: string) => void;
  onRemoveFromExport: (docId: string) => void;
  isDocumentInExportList: (docId: string) => boolean;
  onPdfView: (docId: string, docFilename?: string, initialSearchQuery?: string) => void;
}) {
  const { row, tableType } = selectedRowState;

  function renderContent() {
    switch (tableType) {
      case "CareGaps":
        return <CareGapsSidePanelContent data={row as CareGapRowData} />;

      case "Suspects":
        return <SuspectSidePanelContent data={row as SuspectRowData} />;

      case "AllergyIntolerance":
        return <AllergySidePanelContent data={row as AllergyRowData} />;

      case "CarePlan":
        return <CarePlanSidePanelContent data={row as CarePlanRowData} />;

      case "Condition":
        return <ConditionSidePanelContent data={row as ConditionRowData} />;

      case "Coverage":
        return <CoverageSidePanelContent data={row as CoverageRowData} />;

      case "Encounter":
        return <EncounterSidePanelContent data={row as EncounterRowData} />;

      case "FamilyMemberHistory":
        return <FamilyHistorySidePanelContent data={row as FamilyMemberHistoryRowData} />;

      case "Immunization":
        return <ImmunizationSidePanelContent data={row as ImmunizationRowData} />;

      case "LaboratoryObservations":
        return <LabSidePanelContent data={row as LabRowData} />;

      case "Medication":
        return <MedicationSidePanelContent data={row as MedicationRowData} />;

      case "DiagnosticReport":
        return (
          <NoteSidePanelContent
            data={row as DiagnosticReportRowData}
            resourceType="DiagnosticReport"
          />
        );

      case "ImagingReports":
        return <ImagingReportSidePanelContent data={row as ImagingReportRowData} />;

      case "Procedure":
        return <ProcedureSidePanelContent data={row as ProcedureRowData} />;

      case "RelatedPerson":
        return <RelatedPersonSidePanelContent data={row as RelatedPersonRowData} />;

      case "SocialHistoryObservations":
        return <SocialHistorySidePanelContent data={row as SocialHistoryRowData} />;

      case "VitalSignObservations":
        return <VitalSidePanelContent data={row as VitalRowData} />;

      case "SurveysObservations":
        return <SurveysSidePanelContent data={row as SurveyRowData} />;

      default:
        console.warn(`Unknown tableType: ${tableType}`);
        return null;
    }
  }

  return (
    <SidePanelContextProvider
      fhirSdk={fhirSdk}
      analysisState={analysisState}
      onAddToExport={onAddToExport}
      onRemoveFromExport={onRemoveFromExport}
      isDocumentInExportList={isDocumentInExportList}
      onPdfView={onPdfView}
    >
      {renderContent()}
    </SidePanelContextProvider>
  );
}
